import { FIELD_TYPE_MULTI_LINE_TEXT, FIELD_TYPE_LINE } from '@frontend/constants/type-fields-of-template';
import { MULTI_LINE_PREFIX_ID } from '@frontend/constants/templates-field-prefixes';
import {
  getLockValue,
  getPositioningOption,
  getShadowFabricInstance,
  isInGroup,
  updateBoundingBoxInfo
} from '@frontend/utils';
import { formDataToObject } from '@frontend/lib/utils/form-data-to-object';
import { scaleControlsCornerSize } from '@frontend/constants/fabric-constants';
import parseText from '@/common/preview/parse-text';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import { fabric } from 'fabric';
import { parseJsonData } from "@frontend/services/helpers";
import { RELATED_FIELD_DEFAULT } from "@frontend/constants/related-fields";
import {getGroupNameByAddFieldId} from "@/common/preview/draw-smart-object";
import {ThemesContainerSingleton} from "@/banner_template/themes/ThemesContainerSinglton";
import {getRelatedElementCoords, processMultiRelatedFields, RELATED_DIRECTIONS} from "@/common/preview/related-diagonal-line-helper";
import { CANVAS_EVENTS } from "@frontend/group/modules/editor-control-popup/event-broker";
import { deleteTextLayer } from "@/common/preview/common-helper";
import { calculateTopMostPixels } from "@frontend/services/helpers/calculate-top-most-pixels";
import {handleVerticalAlignment} from "@frontend/constants/alignment";

const PADDING = 0.85;

const getScopePage = () => ShowPageScopeCollector.get(['reorderObjectCanvas', 'canvas_data', 'updateLayoutChanges', 'redrawMultiLineText']);
const countrySelect = $('select[name="country_id"]');
const languageSelect = $('select[name="language_id"]');
let has_smart_objects = false;

const getRectangleDimension = (field, data) => {
  if (!field) {
    return 0;
  }

  let option;
  
  switch (field.type) {
    case 'CustomRect':
      option = {
        Height: field.height * field.scaleY,
        Width: field.width * field.scaleX,
        X: 0,
        Y: 0
      }
      break;
      
    case 'image': {
      const { x, y } = field.getPointByOrigin('left', 'top');

      option = {
        Height: field.height * field.scaleY,
        Width: field.width * field.scaleX,
        X: x,
        Y: y
      }
      break;
    }
      
    default:
      option = JSON.parse(field.options);
  }

  const fieldHeight = option.Height
  const fieldWidth = option.Width


  const scaleY = data[`${field.element_id}_scaleY`] || 1
  const scaleX = data[`${field.element_id}_scaleX`] || 1
  const offsetX = Number(data[`${field.element_id}_offset_x`] || 0)
  const offsetY = Number(data[`${field.element_id}_offset_y`] || 0)

  return {
    rectangleHeight: fieldHeight * scaleY,
    rectangleWidth: fieldWidth * scaleX,
    x: Number(option.X) + offsetX,
    y: Number(option.Y) + offsetY,
  };
}

function fitTextToRectangle(textData) {
  const {
    sizeToFit,
    canvas,
    options,
    color,
    font,
    fields,
    my_rectangle,
    data,
    angle,
    leading,
    tracking = 0,
    maximizeFontSize,
    keepLineBreaks,
    useTemplateFontSize
  } = textData;

  let {
    font_size,
    text_val,
    styles,
  } = textData;

  const rectangle_field = fields.find(field => field.name === my_rectangle);
  if (!sizeToFit || !rectangle_field) {
    return { text_val, font_size, styles };
  }

  const { rectangleHeight, rectangleWidth } = getRectangleDimension(rectangle_field, data);
  const numericAngle = Number(angle);
  const rectangleHeightForText = (numericAngle === 0 || numericAngle === 180) ? rectangleHeight : rectangleWidth;
  const rectangleWidthForText = (numericAngle === 0 || numericAngle === 180) ? rectangleWidth : rectangleHeight;

  const ctx = canvas.getContext('2d');

  if (maximizeFontSize) {
    let templateFontSize;
    // Use template's font size as maximum if specified
    if (useTemplateFontSize) {
      templateFontSize = parseInt(options['Font Size']) || 1000;
    } else {
      templateFontSize = 1000;
    }
    let maxFontSize = templateFontSize;
    
    // If keepLineBreaks is true, we'll split by newlines first
    const textBlocks = keepLineBreaks ? text_val.split('\n') : [text_val];
    
    // Find the maximum font size that works for all blocks
    let smallestMaxFont = maxFontSize;
    let finalText = [];
    
    for (const block of textBlocks) {
      const result = findMaximumFontSize(block, rectangleWidthForText, rectangleHeightForText, font, ctx, leading, maxFontSize);
      smallestMaxFont = Math.min(smallestMaxFont, result.fontSize);
      finalText.push(result.text);
    }

    return {
      text_val: finalText.join('\n'),
      font_size: smallestMaxFont,
      styles
    };
  }

  // If not maximizing font size but keeping line breaks, return original text
  if (keepLineBreaks) {
    return { text_val, font_size, styles };
  }

  font_size = parseInt(options['Font Size']) || font_size;
  const words = text_val.split(/\s+/);
  const resultLines = [];
  let currentLine = words[0];

  // Split text into lines that fit the width
  for (let i = 1; i < words.length; i++) {
    const testLine = currentLine + ' ' + words[i];
    const testLineWidth = getFabricTextWidth(testLine, font_size, font, tracking);

    if (testLineWidth > rectangleWidthForText * PADDING) {
      resultLines.push(currentLine);
      currentLine = words[i];
    } else {
      currentLine = testLine;
    }
  }
  resultLines.push(currentLine);
  text_val = resultLines.join('\n');
  
  return { text_val, font_size, styles };
}

/**
 * 
 * @param text
 * @param maxWidth
 * @param maxHeight
 * @param fontFamily
 * @param ctx
 * @param leading
 * @param high - is max value for text. By default, is 1000 but customer may possible to redefine this property yo use font size from template or smaller to fit
 * @returns {{fontSize: number, text}}
 */
function findMaximumFontSize(text, maxWidth, maxHeight, fontFamily, ctx, leading, high = 1000) {
  let low = 1;
  let bestResult = { fontSize: low, text: text };
  
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const result = tryFontSize(text, mid, maxWidth, maxHeight, fontFamily, ctx, leading);
    
    if (result.fits) {
      bestResult = { fontSize: mid, text: result.text };
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  
  return bestResult;
}

function tryFontSize(text, fontSize, maxWidth, maxHeight, fontFamily, ctx, leading) {
  ctx.font = `${fontSize}px ${fontFamily}`;
  const words = text.split(/\s+/);
  const lines = [];
  let currentLine = words[0];
  
  // Try to fit text with current font size
  for (let i = 1; i < words.length; i++) {
    const testLine = currentLine + ' ' + words[i];
    const metrics = ctx.measureText(testLine);
    
    if (metrics.width > maxWidth * PADDING) {
      lines.push(currentLine);
      currentLine = words[i];
    } else {
      currentLine = testLine;
    }
  }
  lines.push(currentLine);
  
  const totalHeight = lines.length * fontSize * leading;
  const fits = totalHeight <= maxHeight * PADDING && 
               Math.max(...lines.map(line => ctx.measureText(line).width)) <= maxWidth * PADDING;
  
  return {
    fits,
    text: lines.join('\n')
  };
}

function getFabricTextWidth(text, fontSize, fontFamily, tracking = 0) {
  // Create a temporary Fabric text object.
  // Note: This object is never added to any canvas.
  const tempText = new fabric.Text(text, {
    fontSize,
    fontFamily,
    charSpacing: tracking,
    // If you have any other styles you'd like to include, add them here.
  });
  return tempText.width * 0.9;
}

function getColorFromTheme(color, theme) {
  if (!theme || !theme?.attributes) return color;

  let fontColors = theme.attributes.find(attr => attr.name === 'Font Colors');

  if (fontColors && fontColors?.list) {
    let fontColor = fontColors.list.find(item => item.name === color);

    if (fontColor && fontColor?.list?.length) {
      return fontColor.list?.[0].value || color;
    }
  }

  return color;
}

export async function waitForFontsLoaded(fonts = []) {
  try {
    const loadPromises = fonts.map(font =>
      document.fonts.load(font)
    );

    await Promise.all(loadPromises);

  } catch(error) {
    console.error('An error happened in waitForFontsLoaded:', error.message);
  }
}

export async function waitForFont(fontName, fontWeight = 'normal', timeoutMs = 10000) {
  const fontPromise = (async () => {
    try {
      await document.fonts.load(`normal ${fontWeight} 16px "${fontName}"`);
      await document.fonts.ready;
    } catch (error) {
      console.warn(`Font "${fontName}" failed to load:`, error.message);
      // Don't re-throw - allow the text to render with a fallback font
    }
  })();

  const timeoutPromise = new Promise((resolve) =>
    setTimeout(() => {
      resolve();
    }, timeoutMs)
  );

  return Promise.race([fontPromise, timeoutPromise]);
}

export function normalizeAlignment(alignment) {
  if (!alignment) return 'center';

  const normalized = alignment.toLowerCase().trim();

  // Handle various input formats
  switch (normalized) {
    case 'inside':
    case 'inner':
    case 'inset':
      return 'inside'
    case 'outside':
    case 'outer':
    case 'outset':
      return 'outside'
    case 'center':
    case 'middle':
    case 'centered':
    default:
      return 'center'
  }
}

export function createStrokeAlignedText(textBox, outlineShadow) {
  const alignment = normalizeAlignment(outlineShadow.outline.alignment || 'center');
  const strokeWidth = +outlineShadow.outline.thickness || 0;
  const strokeColor = outlineShadow.outline.color;
  const strokeLineJoin = outlineShadow.outline.selectedShape || 'miter';

  // For all alignments, treat as center alignment but handle visually
  if (alignment === 'center' || alignment === 'outside' || alignment === 'inside') {
    handleCenterAlignment(textBox, outlineShadow, strokeColor, strokeWidth, strokeLineJoin, alignment);
    return textBox;
  }

  return textBox;
}

export function applyOutlineShadow(textBox, outlineShadow) {
  if (!textBox || !outlineShadow?.outline) {
    return textBox;
  }

  const normalizedOutlineShadow = {
    outline: {
      thickness: Math.max(0, Number(outlineShadow.outline.thickness) || 0),
      selectedShape: outlineShadow.outline.selectedShape || 'round',
      alignment: normalizeAlignment(outlineShadow.outline.alignment || 'outside'),
      color: outlineShadow.outline.color || 'rgba(0,0,0,1)',
    },
    shadow: {
      blur: Math.max(0, Number(outlineShadow.shadow?.blur) || 0),
      color: outlineShadow.shadow?.color || outlineShadow.outline.color || 'rgba(0,0,0,1)',
      offsetX: Number(outlineShadow.shadow?.offsetX) || 0,
      offsetY: Number(outlineShadow.shadow?.offsetY) || 0,
    },
  };

  // Reset alignment-specific state before applying the new values. This is
  // important when an existing object changes from inside to outside/center.
  textBox.set({
    clipPath: null,
    paintFirst: 'fill',
    shadow: null,
  });

  const alignment = normalizedOutlineShadow.outline.alignment;
  const strokeWidth = normalizedOutlineShadow.outline.thickness;

  textBox.set({
    outlineShadow: normalizedOutlineShadow,
    stroke: normalizedOutlineShadow.outline.color,
    strokeWidth: alignment === 'center' ? strokeWidth : strokeWidth * 2,
    strokeLineJoin: normalizedOutlineShadow.outline.selectedShape,
    strokeUniform: true,
    strokeAlignment: alignment,
    paintFirst: alignment === 'outside' ? 'stroke' : 'fill',
    nonAccumulatingStroke: true,
    shadow: normalizedOutlineShadow.shadow.blur > 0
      ? new fabric.Shadow(normalizedOutlineShadow.shadow)
      : null,
  });

  if (alignment === 'inside') {
    textBox.set('clipPath', createTextClipMask(textBox));
  }

  textBox.setCoords();

  return textBox;
}

export function clearOutlineShadow(textBox) {
  if (!textBox) {
    return textBox;
  }

  const hadOutlineShadow = !!textBox.outlineShadow;

  textBox.set({
    outlineShadow: null,
    stroke: null,
    strokeWidth: 0,
    strokeAlignment: null,
    clipPath: null,
    paintFirst: 'fill',
    nonAccumulatingStroke: false,
  });

  if (hadOutlineShadow) {
    textBox.set('shadow', null);
  }

  textBox.setCoords();
  return textBox;
}

function handleCenterAlignment(textBox, outlineShadow, strokeColor, strokeWidth, strokeLineJoin, alignment = 'center') {
  // Store the original baseLineCoords before any modifications
  const originalBaseLineCoords = textBox.baseLineCoords ? [...textBox.baseLineCoords] : [];

  textBox.set({
    stroke: strokeColor,
    strokeWidth: strokeWidth,
    strokeLineJoin: strokeLineJoin,
    strokeUniform: true,
    outlineShadow: outlineShadow,
    strokeAlignment: alignment, // Store the alignment type
  });

  if (alignment === 'outside' || alignment === 'inside') {
    addGroupLikeMethods(textBox);
  }

  // For outside alignment, create a visual effect without using groups
  if (alignment === 'outside') {
    handleVisualOutsideAlignment(textBox, strokeWidth);
  }

  // For inside alignment, create a visual effect without using groups
  if (alignment === 'inside') {
    handleVisualInsideAlignment(textBox, strokeWidth);
  }

  // Restore the baseLineCoords after setting stroke properties
  textBox.baseLineCoords = originalBaseLineCoords;
}

function addGroupLikeMethods(textBox) {
  // Add getObjects method to mimic group behavior
  textBox.getObjects = function() {
    // For stroke-aligned textboxes, return an array containing just itself
    return [this];
  };

  // Add other group-like methods if needed
  textBox.forEachObject = function(callback) {
    if (typeof callback === 'function') {
      callback(this);
    }
  };

  // Add type property to indicate it's a pseudo-group
  textBox._isStrokeAlignedText = true;
}

function handleVisualOutsideAlignment(textBox, strokeWidth) {
  // Create visual outside effect by adjusting the stroke properties
  // This is a visual trick rather than actual separate objects
  textBox.set({
    strokeWidth: strokeWidth * 2, // Double the stroke for outside effect
    paintFirst: 'stroke', // Draw stroke first, then fill
  });
}

function handleVisualInsideAlignment(textBox, strokeWidth) {
  // Create visual inside effect using clipping mask technique
  // This creates the appearance of inside stroke without groups
  textBox.set({
    strokeWidth: strokeWidth * 2,
    paintFirst: 'fill', // Draw fill first, then stroke
    clipPath: createTextClipMask(textBox), // Clip the stroke to text bounds
  });
}

export function createTextClipMask(textBox) {
  // Create a clone of the textbox to use as clip path
  const clipText = new fabric.Textbox(textBox.text, {
    ...textBox.toObject(),
    fill: 'black',
    stroke: null,
    strokeWidth: 0,
    left: 0,
    top: 0,
    angle: 0,
    originX: 'center',
    originY: 'center',
    selectable: false,
    evented: false,
  });

  return clipText;
}


export async function drawMultiLineText(
  {
    fields,
    positioning_options,
    data,
    canvas,
    shadows,
    id,
    group_fields,
    dimension,
    previewBoundingBoxData,
    skip_these = [],
    isRedrawing = false,
    maximizeFontSize = false,
    keepLineBreaks = true,
    shouldUpdateBoundingBox = true,
    useTemplateFontSize = false
}, canvasEventBroker) {
  // canvas.getObjects().forEach(function (o) {
  //   if (o.id.startsWith(MULTI_LINE_PREFIX_ID)) {
  //     canvas.remove(o);
  //   }
  // });
  const toRemove = canvas.getObjects().filter(o =>
    String(o.id || '').startsWith(MULTI_LINE_PREFIX_ID)
  );
  toRemove.forEach(o => canvas.remove(o));

  const { canvas_data, updateLayoutChanges, redrawMultiLineText } = getScopePage();

  canvas_data[id].multiLineTextCoords = [];

  let i = 0;

  /* used to save bounding box info */
  const formData = new FormData();
  formData.append('type', FIELD_TYPE_MULTI_LINE_TEXT);

  const elementIdsSet = new Set();

  /** Ensure element_id is added only once to element_ids[] */
  function ensureElementId(formData, elementIdsSet, elementId) {
    if (!elementIdsSet.has(elementId)) {
      elementIdsSet.add(elementId);
      formData.append('element_ids[]', elementId);
    }
  }

  /** Writes (or overwrites) bounding-related fields for one textbox */
  function writeBoundingToFormData({
                                     formData,
                                     elementIdsSet,
                                     elementId,
                                     textBox,
                                     fontSize,
                                     offsetX,
                                     offsetY,
                                     autoPosition,
                                   }) {
    ensureElementId(formData, elementIdsSet, elementId);

    const boundingBox = textBox.getBoundingRect();
    const lineHeights = Math.max(...(textBox.__lineHeights || [0]));

    formData.set(`${elementId}_bounding_data`, JSON.stringify(boundingBox));
    formData.set(`${elementId}_width`, String(textBox.isCurved ? textBox._straightTextWidth : textBox.width));
    formData.set(`${elementId}_height`, String(textBox.height));
    if (textBox.fixedHeight) {
      formData.set(`${elementId}_container_height`, String(textBox.fixedHeight));
    }
    formData.set(`${elementId}_line_heights`, lineHeights.toFixed(2));
    formData.set(`${elementId}_fontsize`, String(fontSize));
    formData.set(`${elementId}_offset_x`, String(offsetX));
    formData.set(`${elementId}_offset_y`, String(offsetY));
    formData.set(`${elementId}_auto_position`, String(autoPosition));
  }


  const filterFields = fields.filter(
    ({ type }) => [FIELD_TYPE_MULTI_LINE_TEXT, FIELD_TYPE_LINE].includes(type)
  );
  const pendingRelated = [];
  for (let index = 0; index < filterFields.length; index++) {
    const field = filterFields[index];


    if (skip_these.includes(field.name))
      continue;

    const options = JSON.parse(field.options);
    const groupName = getGroupNameByAddFieldId(
      String(field.id),
      canvas_data[id]
    );

    if ((field.type === 'Multi Line Text') && (!group_fields || isInGroup(group_fields, field.name))) {
      const positioningOption = getPositioningOption(
        positioning_options,
        data,
        fields,
        field
      );

      const selectedTheme = await ThemesContainerSingleton.getThemeById(data?.theme);

      // should be only string data type
      let text_val = (data[field.element_id] ?? '') + '';

      text_val = text_val.replace(/<br>/g, '\n');
      // tag color created to caught custom bullet's color
      let custom_bullet_color = text_val.match(/<color='(.*?)'>(.*?)<\/color>/i);
      let bulletPositions = null;
      let bullet = null
      let bullet_obj = {}

      if (custom_bullet_color && custom_bullet_color[1]) {
        //get bullet to find all positions to apply color
        bullet = custom_bullet_color[2]
        bullet_obj.bullet = bullet
        bullet_obj.custom_bullet = custom_bullet_color

        custom_bullet_color = custom_bullet_color[1]
        //clear tag from text
        text_val = text_val.replace(/<color='(.*?)'>(.*?)<\/color>/g, '$2');
        //get bullet positions 
        bulletPositions = [...text_val.matchAll(new RegExp(bullet, "g"))].map(m => m.index);
        bullet_obj.bulletPositions = bulletPositions
      } else {
        custom_bullet_color = '';
      }

      if (data.product_texts) {
        const country = countrySelect.val();
        const language = languageSelect.val();
        const product_texts = JSON.parse(data.product_texts);
        const text_data = product_texts[country + '_' + language];
        if (text_data) {
          text_val = text_data.field_texts[i];
        }
      }

      const color = getColorFromTheme(data[`${field.element_id}_color`], selectedTheme);

      let font = data[`${field.element_id}_font`];
      font = font ? font : options['Font'] ? options['Font'] : 'Proxima-Nova-Semibold';
      const isCurved = data[`${field.element_id}_is_curved`] || false;
      const parseFontSize = isCurved ? parseFloat : parseInt;
      let font_size = data[`${field.element_id}_fontsize`];
      font_size = font_size ? parseFontSize(font_size) : parseFontSize(options['Font Size']);
      let offset_x = data[`${field.element_id}_offset_x`];
      let offset_y = data[`${field.element_id}_offset_y`];
      const angle = data[`${field.element_id}_angle`];
      const opacity = data[`${field.element_id}_opacity`];
      const isShadow = +data[`${field.element_id}_shadow`];
      const alignment = data[`${field.element_id}_alignment`];
      let {x, y, baseX, baseY} = initCoords(data, dimension, field, options);
      const initX = x;
      const initY = y;
      let width = +data[`${field.element_id}_width`] || +options['Width'];
      let height  = +data[`${field.element_id}_height`] || +options['Height'];
      const containerHeight = +data[`${field.element_id}_container_height`] || height;
      const fieldScaleX = +data[`${field.element_id}_scaleX`] || 1;
      const fieldScaleY = +data[`${field.element_id}_scaleY`] || 1;
      const visible = data[`${field.element_id}_visible`] ?? !options['Hidden'];
      const relatedField = parseJsonData(data[`${field.element_id}_related_field`], RELATED_FIELD_DEFAULT);
      let multiRelatedFields = parseJsonData(data[`${field.element_id}_multi_related_field`] || '', parseJsonData(options['Option4'] || '', {}));
      let customBullet = parseJsonData(data[`${field.element_id}_custom_bullet`] || '', {});
      const alignToField = data[`${field.element_id}_align_to_field`] || options['Option1'];
      let outlineShadow = parseJsonData(data[`${field.element_id}_custom_outline_shadow`], null)
      const gradient = parseJsonData(data[`${field.element_id}_custom_gradient`], null)
      const maxChars = Number(data[`${field.element_id}_max_chars`] || parseInt(options['Max Chars']) || 10000);
      const curvedDiameter = Number(data[`${field.element_id}_diameter`]) || 1000;
      const curvedKerning = 1.5;
      const splitBulletsChildren = data[`${field.element_id}_split_bullets_children`] || null;
      const outerGlow = data[`${field.element_id}_outer_glow`]
        ? JSON.parse(data[`${field.element_id}_outer_glow`])
        : null;
      const textAlign = alignment
        ? alignment
        : options['Alignment']
          ? options['Alignment']
          : 'left,top';
      // check if we need to enable vertical alignment for this field
      // default - false
      const isEnableVerticalAlignment = data[`${field.element_id}_is_enable_vertical_alignment`] ?? false;

      if (groupName && canvas_data[id].smartObjCoords[groupName]) {
        x += +canvas_data[id].smartObjCoords[groupName]['X'];
        y += +canvas_data[id].smartObjCoords[groupName]['Y'];
      }

      let tracking = data[`${field.element_id}_tracking`];
      tracking = tracking ? tracking : (options['Text Tracking'] ? parseInt(options['Text Tracking']) : 0);

      let leading = data[`${field.element_id}_leading`];
      leading = leading ? leading : (options['Leading'] ? parseFloat(options['Leading']) : 1);

      let stroke = data[`${field.element_id}_stroke`];

      if (stroke) {
        try {
          stroke = JSON.parse(stroke);
        } catch (error) {
          stroke = undefined;
        }
      }

      // todo investigate affect to size to fit functionality
      if (positioningOption) {
        x = positioningOption.x == null ? x : positioningOption.x;
        y = positioningOption.y == null ? y : positioningOption.y;
        width = positioningOption.width == null ? width : positioningOption.width;
      }

      const sizeToFit = !!options['Size To Fit'];

      // for related fields we need to draw them even if the text is blank
      if ( (text_val && text_val != '')  || field.is_related ) {
        const parsedTextData = parseText(text_val, color ? color : options['Font Color']);
        const hasRawHtmlMarkup = /<[^>]+>|&nbsp;?/i.test(text_val);
        const hasStoredStyles = !!data[`${field.element_id}_styles`];

        if (hasRawHtmlMarkup || parsedTextData.text !== text_val) {
          text_val = parsedTextData.text;
        }

        let styles = hasStoredStyles
          ? data[`${field.element_id}_styles`]
          : parsedTextData.styles;

        // Find and load all fonts used in the textbox to prevent character overlapping issues
        const fonts = new Set();

        for (const lineIndex in styles) {
          const line = styles[lineIndex];

          for (const charIndex in line) {
            const style = line[charIndex];

            if (style.fontFamily) {
              const fontSize = style.fontSize || font_size;

              fonts.add(`${fontSize}px "${style.fontFamily}"`);
            }
          }
        }

        if (fonts.size) {
          await waitForFontsLoaded(Array.from(fonts));
        }

        // Normalize the styles matrix so each line remains aligned with text character indexes.
        // Empty placeholders must be preserved because downstream code relies on positional
        // correspondence between style entries and characters. If a line is not an array,
        // return an empty line so the structure stays predictable without shifting indexes.
        if (Array.isArray(styles)) {
          styles = styles.map(line => {
            if (!Array.isArray(line)) {
              return [];
            }

            return line.map(style => {
              return style && typeof style === 'object' && !Array.isArray(style)
                ? style
                : {};
            });
          });
        }
        // Smart object (AKA centering text to a rectangle)
        const my_rectangle = data[`${field.element_id}_align_to_field`] || options['Option1'];

        // size to fit - V4
        const fitData = fitTextToRectangle({
          sizeToFit,
          canvas,
          text_val,
          font_size,
          options,
          color,
          font,
          fields,
          my_rectangle,
          data,
          angle,
          leading,
          styles,
          tracking,
          maximizeFontSize,
          keepLineBreaks,
          useTemplateFontSize
        });

        text_val = fitData.text_val;
        font_size = fitData.font_size;
        styles = fitData.styles;

        let left = canvas_data[id].spacingFieldPosition[field.name]
          ? canvas_data[id].spacingFieldPosition[field.name].x
          : x + parseFloat(offset_x);

        let top = y + parseFloat(offset_y);

        let flipped = data[`${field.element_id}_curved_flipped`] || false;

        //hack to check if font load - need to calculate width of chars for correct positions inside Textbox
        await waitForFont(font).then(() => {});

        const initialWidthKey = `${field.element_id}_fit_detected_width_pending`;
        const fitDetectedWidth = [true, 1, '1', 'true'].includes(data[initialWidthKey]);
        const preserveDetectedLineBreaks = [true, 1, '1', 'true'].includes(
          data[`${field.element_id}_preserve_detected_line_breaks`]
        );
        if (fitDetectedWidth || preserveDetectedLineBreaks) {
          // Text measures explicit lines without wrapping. Include character styles
          // and tracking so the imported width fits the fonts used by the editor.
          const measuredText = new fabric.Text(text_val, {
            fontSize: font_size,
            fontFamily: font,
            charSpacing: +tracking,
            lineHeight: leading,
            styles,
          });
          width = Math.max(width, Math.ceil(measuredText.calcTextWidth()) + 2);
        }

        let textBox = new fabric.Textbox(text_val, {
          detectedTextReflow: preserveDetectedLineBreaks
            || [true, 1, '1', 'true'].includes(data[`${field.element_id}_detected_text_reflow`]),
          id: MULTI_LINE_PREFIX_ID + index,
          groupName: groupName,
          element_id: field.element_id,
          order: parseInt(options['Order']),
          top,
          left,
          width,
          scaleX: fieldScaleX,
          scaleY: fieldScaleY,
          originX: 'center',
          originY: 'center',
          textAlign,
          fontSize: font_size,
          opticalAlignment: [true, 1, '1', 'true'].includes(data[`${field.element_id}_optical_alignment`]),
          fontColor: color ? color : options['Font Color'],
          fill: color ? color : options['Font Color'],
          lineHeight: leading,
          fontFamily: font,
          charSpacing: +tracking,
          opacity,
          styles,
          fontSelector: options['Font Selector'] === 'Yes',
          colorSelector: options['Color Selector'] === 'Yes',
          fontThemeSelector: options['Font Selector'] === 'Theme',
          fieldId: field.id,
          lockScalingFlip: true,
          templateField: canvas_data[id].templateInstance.getFieldById(field.id),
          sizeToFit: sizeToFit,
          angle: angle ? parseFloat(angle) : 0,
          visible,
          relatedField,
          bullet_obj,
          alignToField,
          isCurved,
          flipped,
          curvedDiameter,
          curvedKerning,
          maxChars,
          updateLayoutChanges,
          deleteLayer: deleteTextLayer,
          outerGlow,
          redrawMultiLineText
        });
        // Initial rendering batches geometry updates in previewBoundingBoxData.
        // Save the fitting flags in that same batch so later renders do not fit again.
        if ((fitDetectedWidth || preserveDetectedLineBreaks) && (shouldUpdateBoundingBox || previewBoundingBoxData)) {
          const fittedSettings = {
            [`${field.element_id}_width`]: textBox.width,
            [initialWidthKey]: false,
          };
          if (preserveDetectedLineBreaks) {
            // Replace the legacy no-wrap mode with reflow after this one-time fit.
            // Resizing can then wrap and unwrap without storing soft wraps as newlines.
            fittedSettings[`${field.element_id}_preserve_detected_line_breaks`] = false;
            fittedSettings[`${field.element_id}_detected_text_reflow`] = true;
          }
          Object.entries(fittedSettings).forEach(([key, value]) => {
            data[key] = value;
            if (previewBoundingBoxData) {
              formData.set(key, value);
            } else {
              updateLayoutChanges(id, key, value);
            }
          });
        }
        const heightByContent = textBox.height;
        //enable vertical alignment, this option should set backend during template creation
        if (isEnableVerticalAlignment){
          textBox.height = Math.max(containerHeight, heightByContent);
          textBox.fixedHeight = containerHeight;
          textBox.heightByContent = heightByContent;
          textBox.isSupportVerticalAlign = true;
        }

        if (gradient) {
          textBox.set({gradientSettings: gradient});
          await enhanceTextWithGradient(textBox, gradient);
        }

        if (stroke && !outlineShadow) {
          // Only create outline shadow if there's actually an outline
          if (stroke && stroke.color && stroke.width > 0) {
            outlineShadow = {
              outline: {
                color: stroke.color,
                thickness: stroke.width,
                selectedShape: stroke.strokeLineJoin || 'miter',
                alignment: normalizeAlignment(stroke.position || 'outside'),
              },
              shadow: {
                color: stroke.color,
                blur: 0,
                offsetX: 0,
                offsetY: 0,
              },
            }
          }
        }

        // Fixes special symbols (e.g., ® ™ ©) for fonts that don't support them well:
        // detects these characters, optionally preloads a fallback font, and applies per-character
        // styles (smaller fontSize + negative deltaY) so the symbols render like superscripts,
        // then forces Fabric to re-measure and re-render the textbox.
        await fixSpecialSymbolsOnRender(textBox, {
          baseFontPrefixes: ['UniversLTStd-LightCn'],
          fallbackFontFamily: null, 
          instanceId: id
        })

        textBox.on('changed', function () {
          this.styles = this.generateResolvedStyleArray();
        });
        textBox.set({ shadow: getShadowFabricInstance(id, textBox.element_id, shadows, isShadow) });
        textBox.set({ locked: getLockValue(id, textBox.element_id) });
        textBox.setCoords(); 

        if (outlineShadow && outlineShadow.outline) {
          applyOutlineShadow(textBox, outlineShadow);
        }

        textBox.setControlsVisibility({
          tr: true,
          br: true,
          bl: true,
          ml: true,
          mt: true,
          mr: true,
          mb: true,
          mtr: true,
        });
        
        // set custom color to bullet
        // and set content: 'bullet_color' for catch this variable on backend to export layer with needed color
        if (custom_bullet_color) {
          let bulletStyleObj = {fill: custom_bullet_color}
          if (bulletPositions) {
            bulletPositions.forEach((pos, i) => {
              if (i === 0) {
                bulletStyleObj.content = 'bullet_color'
              }
              textBox.setSelectionStyles(bulletStyleObj, pos, pos+1)   
            })
          } else {
            textBox.setSelectionStyles({fill: custom_bullet_color, content: 'bullet_color'}, 0, 1)
          }
        }

        scaleControlsCornerSize(textBox);
        if (isEnableVerticalAlignment){
          textBox.set('height', Math.max(containerHeight, heightByContent));
        }
        canvas.add(textBox);
        textBox.set({
          originX: 'left',
          originY: 'top',
        });
        textBox.setCoords(); 

        try {
          let parsedOption = parseJsonData(data[`${field.element_id}_related_field`], {});
          if (parsedOption && parsedOption.field && parsedOption.field.trim() !== "") {
            multiRelatedFields[parsedOption.field] = {
              margin: parsedOption && parsedOption.margin !== undefined ? parsedOption.margin : 5,
              direction: RELATED_DIRECTIONS.TOP_TO_BOTTOM_POSITION.value,
              move_down_only: parsedOption && parsedOption.move_down_only !== undefined ? parsedOption.move_down_only : false,
            }
          }
        } catch (e) {
          // console.error("Invalid JSON string:", e.message);
        }

        multiRelatedFields = processMultiRelatedFields(multiRelatedFields);
        
        if (multiRelatedFields.length > 0) {
          pendingRelated.push({
            textBox,
            field,
            x,
            y,
            customBullet,
            multiRelatedFields,
            fontSize: font_size,
            // keep refs for later offset updates
            getOffsets: () => ({ offset_x, offset_y }),
            setOffsets: (nx, ny) => { offset_x = nx; offset_y = ny; },
          });
        }


        if (!Object.hasOwn(data, `${field.element_id}_auto_position`)) {
          data[`${field.element_id}_auto_position`] = false;
        }

        if (!Object.hasOwn(data, `multi_line_text_auto_align_position`)) {
          data.multi_line_text_auto_align_position = false;
        }

        data.multi_line_text_auto_align_position = checkMLTAutoAlignPosition(data);
        
        if (my_rectangle) {
          has_smart_objects = true;
          // If the field is not found among the base fields, search for an additional field
          const rectangle_field = fields.find(field => field.name === my_rectangle) 
            || canvas.getObjects().find(field => field.name === my_rectangle);

          // if an object with that name was found center the text to it.
          if ((rectangle_field && isRedrawing) || (rectangle_field && data.multi_line_text_auto_align_position)) {
            const related_object = canvas.getObjects().find(cObject => cObject?.templateField && cObject.templateField.id === rectangle_field.id);
            const fieldIsNotRectangle = rectangle_field.type !== 'Rectangle'
            const rectangleDimsAndPos = getRectangleDimension(rectangle_field, data)
            const newLeftAndTop = placeTextboxAtRectangleCenter(rectangleDimsAndPos, textBox, fieldIsNotRectangle);
            if (related_object) {
              textBox.setPositionByOrigin(related_object.getCenterPoint(), 'center', 'center');
            } else {
              textBox.left = newLeftAndTop.left;
              textBox.top = newLeftAndTop.top;
            }
            textBox.setCoords()

            // calculate new offsets so we can save them.
            offset_x = textBox.left - x;
            offset_y = textBox.top - y;
          }
        }

        textBox.set({ autoPosition: false })

        /* update bounding box info */
        writeBoundingToFormData({
          formData,
          elementIdsSet,
          elementId: field.element_id,
          textBox,
          fontSize: font_size,
          offsetX: offset_x,
          offsetY: offset_y,
          autoPosition: data[`${field.element_id}_auto_position`],
        });

        canvas_data[id].multiLineTextCoords[index] = { x: baseX + (x - initX), y: baseY + (y - initY) };

        const pointLeftTop = textBox.getPointByOrigin('left', 'top');
        const lockMoveAfterChanged = function (e) {
          this.setPositionByOrigin(pointLeftTop, 'left', 'top');
        };

        if (options['Moveable'] !== 'Yes') {
          textBox.on('moving', lockMoveAfterChanged);
        }

        const verticalAlign = textAlign.split(',')[1] ? textAlign.split(',')[1] : 'top';
        if (textBox.isSupportVerticalAlign) {
          handleVerticalAlignment(textBox, verticalAlign, textBox.height - heightByContent);
        }
        
        if (textBox && splitBulletsChildren) {
          canvasEventBroker.fire(CANVAS_EVENTS.REDRAW_ADDITIONAL_MULTI_LINE_TEXT, {parentElementId: textBox.element_id, parentTop: textBox.top, splitBulletsChildren: splitBulletsChildren})
        }
      }
      i++;
    }
  }
  function findRelatedOnCanvas(canvas, name) {
    return canvas.getObjects().find(obj =>
      (obj.templateField && obj.templateField.name === name) ||
      (obj.name && obj.name === name)
    );
  }

  // Apply related positioning after all textboxes exist.
  // We do a few passes to support chains (A depends on B depends on C).
  for (let pass = 0; pass < 3; pass++) {
    let movedSomething = false;

    for (const job of pendingRelated) {
      const { textBox, field, x, y, customBullet, multiRelatedFields, setOffsets, fontSize, getOffsets } = job;
      // If user already moved it manually - don't touch.
      const movedRaw = data?.[`${field.element_id}_text_moved`];
      const isMoved = movedRaw === true || movedRaw === 1 || movedRaw === '1' || movedRaw === 'true';
      if (isMoved) continue;

      const prev = getOffsets ? getOffsets() : {};
      let latestNx = Number.isFinite(+prev.offset_x) ? +prev.offset_x : 0;
      let latestNy = Number.isFinite(+prev.offset_y) ? +prev.offset_y : 0;

      for (const modifyOptions of multiRelatedFields) {
        const related = findRelatedOnCanvas(canvas, modifyOptions.field);
        if (!related) continue;

        const moveDownOnly = (
          modifyOptions?.move_down_only === true ||
          modifyOptions?.move_down_only === 1 ||
          modifyOptions?.move_down_only === '1'
        );
        
        textBox.set(getRelatedFieldTextboxOptions(textBox, related, modifyOptions, customBullet));

        if (moveDownOnly) {
          const baseTop = Number(y) || 0;
          const nextTop = Number(textBox.top);

          if (Number.isFinite(nextTop) && nextTop < baseTop) {
            textBox.top = baseTop;
          }
        }
        
        textBox.setCoords();

        latestNx = (Number(textBox.left) || 0) - (Number(x) || 0);
        latestNy = (Number(textBox.top) || 0) - (Number(y) || 0);

        setOffsets(latestNx, latestNy);

        movedSomething = true;
      }

      writeBoundingToFormData({
        formData,
        elementIdsSet,
        elementId: field.element_id,
        textBox,
        fontSize: Number.isFinite(+job.fontSize) ? +job.fontSize : (Number(textBox.fontSize) || 0),
        offsetX: latestNx,
        offsetY: latestNy,
        autoPosition: data?.[`${field.element_id}_auto_position`],
      });
    }

    if (!movedSomething) break;
  }

  /* UPDATE BOUNDING BOX INFO */
  if (previewBoundingBoxData) {
    previewBoundingBoxData.data.push(formDataToObject(formData));
  } else if (shouldUpdateBoundingBox) {
    updateBoundingBoxInfo(formData, id);
  }
  
  const { reorderObjectCanvas } = getScopePage();
  reorderObjectCanvas(canvas);
}

async function waitForCanvasObjectByName(canvas, name, tries = 20, delayMs = 200) {
  for (let i = 0; i < tries; i++) {
    const obj = canvas.getObjects().find(o =>
      (o.templateField && o.templateField.name === name) ||
      (o.name && o.name === name)
    );
    if (obj) return obj;
    await new Promise(r => setTimeout(r, delayMs));
  }
  return null;
}

function getStyleAtIndex(textBox, index) {
  const styles = textBox.styles;
  if (!styles || typeof styles !== 'object') return null;

  const text = textBox.text || '';
  let line = 0;
  let col = 0;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (ch === '\n') {
      line++;
      col = 0;
      continue;
    }

    if (i === index) {
      return styles?.[line]?.[col] || null;
    }

    col++;
  }

  return null;
}

function isAlreadySuperscript(style, baseFontSize, sizeScale, raiseScale) {
  if (!style || typeof style !== 'object') return false;

  // If deltaY already raises it, treat as superscript
  if (typeof style.deltaY === 'number' && style.deltaY < 0) return true;

  // Or if fontSize already looks like superscript
  if (typeof style.fontSize === 'number' && style.fontSize <= baseFontSize * (sizeScale + 0.05)) {
    return true;
  }

  return false;
}

export async function fixSpecialSymbolsOnRender(textBox, opts = {}) {
  if (!textBox || typeof textBox.text !== 'string') return false;

  const {
    baseFontPrefixes = ['UniversLTStd-LightCn'],
    symbolsRegex = /[®™©°±µ•…]/g,
    fallbackFontFamily = null, // e.g. 'Noto Sans Symbols 2' or null
    sizeScale = 0.6,
    raiseScale = 0.3,
    preloadTimeoutMs = 8000,
  } = opts;

  const normalize = (s) => (s || '').toString().trim().replace(/^["']|["']$/g, '');
  const baseFont = normalize(textBox.fontFamily);

  const isTargetFont = baseFontPrefixes.some((p) => baseFont.startsWith(p));
  if (!isTargetFont) return false;

  const text = textBox.text;

  if (!symbolsRegex.test(text)) {
    symbolsRegex.lastIndex = 0;
    return false;
  }
  symbolsRegex.lastIndex = 0;

  if (fallbackFontFamily) {
    await loadFontSafe(fallbackFontFamily, '®™©', preloadTimeoutMs);
  }

  const scale = textBox.scaleY || 1;
  const baseFontSizeUnscaled = (textBox.fontSize || 16);
  const baseFontSize = (textBox.fontSize || 16) * scale;

  const markFontSize = (baseFontSize * sizeScale) / scale;
  const deltaY = (-baseFontSize * raiseScale) / scale;

  for (const m of text.matchAll(symbolsRegex)) {
    const idx = m.index;
    if (idx == null) continue;

    const currentStyle = getStyleAtIndex(textBox, idx);

    if (isAlreadySuperscript(currentStyle, baseFontSizeUnscaled, sizeScale, raiseScale)) {
      continue;
    }

    const patch = {
      fontSize: markFontSize,
      deltaY: deltaY,
    };

    if (fallbackFontFamily) patch.fontFamily = fallbackFontFamily;

    textBox.setSelectionStyles(patch, idx, idx + 1);
    // TODO: POR-11561 the following line creates unsaved changes, maybe we need to force-save them instead of just adding to changes list
    // textBox.updateLayoutChanges(opts.instanceId, textBox.element_id + '_styles', textBox.styles);
  }

  symbolsRegex.lastIndex = 0;

  // force redraw
  textBox.initDimensions?.();
  textBox.dirty = true;
  textBox.canvas?.requestRenderAll();

  return true;
}

async function loadFontSafe(fontFamily, sampleText = '®™©', timeoutMs = 8000) {
  try {
    const fontSpec = `16px "${fontFamily}"`;
    const fontPromise = (async () => {
      await document.fonts.load(fontSpec, sampleText);
      await document.fonts.ready;
    })();
    const timeoutPromise = new Promise((r) => setTimeout(r, timeoutMs));
    await Promise.race([fontPromise, timeoutPromise]);
  } catch (e) {
    //nothing
  }
}


function placeTextboxAtRectangleCenter(rectangleDimsAndPos, textbox, fieldIsNotRectangle = false) {

  const rectCenter = {
    x: rectangleDimsAndPos.x + rectangleDimsAndPos.rectangleWidth / 2,
    y: rectangleDimsAndPos.y + rectangleDimsAndPos.rectangleHeight / 2,
  }

  const rotationInRadians = fabric.util.degreesToRadians(textbox.angle);
  const sinOfRotation = Math.sin(rotationInRadians);
  const cosOfRotation = Math.cos(rotationInRadians);

  const correctedX = (textbox.width / 2) * cosOfRotation - (textbox.height / 2) * sinOfRotation;
  const correctedY = (textbox.width / 2) * sinOfRotation + (textbox.height / 2) * cosOfRotation;

  if (textbox.angle === 0 || textbox.angle === 180) {
    const newLeft = rectCenter.x - textbox.width / 2;
    const newTop = (rectCenter.y - textbox.height / 2) + (fieldIsNotRectangle ? 0 : 6);

    textbox.set({ left: newLeft, top: newTop });
    
    return { left: newLeft, top: newTop };
  } else if (textbox.angle === 90 || textbox.angle === 270) {
    const newLeft = (rectCenter.x - correctedX) + (fieldIsNotRectangle ? 0 : 6);
    const newTop = rectCenter.y - correctedY;

    textbox.set({
      left: newLeft,
      top: newTop
    });

    return { left: newLeft, top: newTop };
  }
}

function checkAutoPosition(field, data) {
  return !!(data[`${field.element_id}_auto_position`] && data[`${field.element_id}_auto_position`] !== 'false');
}

function checkMLTAutoAlignPosition(data) {
  return !!(data[`multi_line_text_auto_align_position`] && data[`multi_line_text_auto_align_position`] !== 'false');
}

function getRelatedFieldTextboxOptions(textBox, relatedFieldElement, modifyOptions, customBullet) {
  let coords = {};
  let newCoords = {};
  let eq = true;
  let i = 0;

  do {
    newCoords = getRelatedElementCoords(
      coords,
      textBox,
      modifyOptions,
      relatedFieldElement,
      parseInt(customBullet.width * 2/5 || '0') + parseInt(customBullet.width || '0')
    );
    eq = JSON.stringify(coords) === JSON.stringify(newCoords);
    coords = {...newCoords};
    i++;
  } while(!eq && i < 5);

  return newCoords;
}

function initCoords(data, dimension, field, options) {
  const boundingData = parseJsonData(data[`${field.element_id}_bounding_data`] || '{}')
  const forceBoundingData = data[`${field.element_id}_force_bounding_data`] ?? false;
  
  const baseX = parseFloat(options['X']) + dimension[4].x;
  const baseY = parseFloat(options['Y']) + dimension[4].y;

  let x = forceBoundingData
    ? parseFloat(boundingData?.left || 0)
    : baseX;

  let y = forceBoundingData
    ? parseFloat(boundingData?.top || 0)
    : baseY;

  return { x, y, baseX, baseY };
}

export async function drawTextWithGradient(textObject, gradientData) {
  if (!gradientData || gradientData.solidColor) {
    const color = gradientData?.colors?.[0] || textObject.fill || "#000000"
    const opacity = gradientData?.transparency ? (100 - gradientData.transparency) / 100 : 1
    const finalColor = addOpacityToColor(color, opacity)
    textObject.set({
      fill: finalColor,
      gradientData: null
    });
    delete textObject.gradientData;

    return textObject
  }

  const gradient = await createTextGradient(textObject, gradientData)
  textObject.set({
    fill: gradient,
    gradientData: gradientData
  })

  textObject.dirty = true
  if (textObject.canvas) {
    textObject.canvas.requestRenderAll()
  }

  return textObject
}

async function createTextGradient(textObject, gradientData) {
  const { type, angle = 0, colors = [], colorSteps = [], transparency = 0 } = gradientData

  const coords = type === 'linear' ? await getLinearGradientCoords(textObject, angle) : getRadialGradientCoords(textObject)

  const colorStops = colors.map((color, index) => {
    const offset =
      colorSteps[index] !== undefined
        ? Math.max(0, Math.min(1, colorSteps[index] / 100))
        : index / Math.max(1, colors.length - 1)
    const opacity = Math.max(0, Math.min(1, (100 - transparency) / 100))

    return {
      offset: offset,
      color: addOpacityToColor(color, opacity),
    }
  })

  colorStops.sort((a, b) => a.offset - b.offset)

  return new fabric.Gradient({
    type: type,
    coords: coords,
    colorStops: colorStops,
  })
}

async function getLinearGradientCoords(obj, angle) {
  const topMost = await calculateTopMostPixels(
    obj.fontFamily, 
    obj.fontSize, 
    obj.text 
  );

  const width = obj.width || obj.getBoundingRect().width || 100;
  const height = obj.height || obj.getBoundingRect().height || 50;

  const centerX = width / 2;
  const centerY = height / 2;

  const angleRad = (angle * Math.PI) / 180;
  const dx = Math.cos(angleRad);
  const dy = Math.sin(angleRad);

  const offsetStartX = topMost.leftMostX * dx;
  const offsetStartY = topMost.topMostY * dy;
  const offsetEndX = topMost.rightMostX * dx;
  const offsetEndY = topMost.bottomMostY * dy;

  const scaleX = dx !== 0 ? (width / 2) / Math.abs(dx) : Infinity;
  const scaleY = dy !== 0 ? (height / 2) / Math.abs(dy) : Infinity;
  const scale = Math.min(scaleX, scaleY);

  return {
    x1: centerX - dx * scale + offsetStartX,
    y1: centerY - dy * scale + offsetStartY,
    x2: centerX + dx * scale - offsetEndX,
    y2: centerY + dy * scale - offsetEndY
  };
}


function getRadialGradientCoords(textObject) {
  const width = textObject.width || textObject.getBoundingRect().width || 100
  const height = textObject.height || textObject.getBoundingRect().height || 50

  const centerX = width / 2
  const centerY = height / 2
  const radius = Math.max(width, height) / 2

  return {
    x1: centerX,
    y1: centerY,
    x2: centerX,
    y2: centerY,
    r1: 0,
    r2: radius,
  }
}

function addOpacityToColor(color, opacity) {
  if (!color) return "#000000"

  // Handle hex colors
  if (color.startsWith("#")) {
    const cleanColor = color.length === 7 ? color : color.substring(0, 7)
    const alpha = Math.round(Math.max(0, Math.min(1, opacity)) * 255)
      .toString(16)
      .padStart(2, "0")
    return cleanColor + alpha
  }

  // Handle rgba colors
  if (color.startsWith("rgba")) {
    return color.replace(/[\d.]+\)$/g, `${opacity})`)
  }

  // Handle rgb colors
  if (color.startsWith("rgb")) {
    return color.replace("rgb", "rgba").replace(")", `, ${opacity})`)
  }

  return color
}

export async function enhanceTextWithGradient(textObject, gradientSettings) {
  if (!textObject || !gradientSettings) {
    return textObject
  }

  if (!textObject.type || !["text", "textbox", "i-text"].includes(textObject.type)) {
    return textObject
  }

  if (textObject.styles) {
    updateTextStylesColor(textObject);
  }

  if (gradientSettings.type === 'linear' || gradientSettings.type === 'radial') {
    return drawTextWithGradient(textObject, gradientSettings)
  }

  if (gradientSettings.solidColor || gradientSettings.colors?.length > 0) {
    const color = gradientSettings.colors[0] || "#000000"
    const opacity = gradientSettings.transparency ? (100 - gradientSettings.transparency) / 100 : 1
    const finalColor = addOpacityToColor(color, opacity)
    textObject.set({
      fill: finalColor,
      gradientData: null,
    });
    
    delete textObject.gradientData;

    textObject.dirty = true
    if (textObject.canvas) {
      textObject.canvas.requestRenderAll()
    }
  }

  return textObject
}

export function updateTextGradient(textObject) {
  if (textObject.gradientData) {
    return drawTextWithGradient(textObject, textObject.gradientData)
  }
  return textObject
}

export function removeTextGradient(textObject) {
  textObject.set("fill", "#000000")
  delete textObject.gradientData
  return textObject
}

function updateTextStylesColor(textObject) {
  if (!textObject.styles) return;
  
  for (let lineIndex in textObject.styles) {
    const lineStyles = textObject.styles[lineIndex];
    if (lineStyles && typeof lineStyles === 'object') {
      for (let charIndex in lineStyles) {
        if (lineStyles[charIndex]) {
          // Remove fill property while preserving others
          const { fill, ...otherStyles } = lineStyles[charIndex];
          textObject.styles[lineIndex][charIndex] = otherStyles;
        }
      }
    }
  }
  
  // In Fabric.js, you need to mark the object as dirty and trigger rendering
  textObject.dirty = true;
  
  // If you're using canvas, you might need to render it
  if (textObject.canvas) {
    textObject.canvas.requestRenderAll();
  }
}
